import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import {
  Badge,
  DataTable,
  Modal,
  type Column
} from "../../components/index.js";
import type { BoxTracking, PlanSummary, Shipment } from "../../types.js";
import { planBadge } from "../../lib/plan-status.js";
import {
  deriveDestinationLabel,
  formatDateTime,
  referenceSuffix
} from "../../lib/shipments-view.js";

/**
 * Detalhe da remessa (Design.md §5.4 "Visualizar" + §6.5 rastreamento por caixa).
 * Modal enxuto: dados do plano (GET /:id) + shipments com rastreamento por caixa
 * (GET /:id/shipments). Não é uma rota/tela completa (fora do escopo de §5).
 */
export interface PlanDetailModalProps {
  planId: string | null;
  onClose: () => void;
}

interface DetailState {
  plan: PlanSummary;
  shipments: Shipment[];
}

export function PlanDetailModal({
  planId,
  onClose
}: PlanDetailModalProps): ReactNode {
  const { api } = useAuth();
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (planId === null) {
      setDetail(null);
      setErro(null);
      return;
    }

    let cancelled = false;
    setCarregando(true);
    setErro(null);
    setDetail(null);

    Promise.all([api.getPlan(planId), api.getPlanShipments(planId)])
      .then(([planRes, shipmentsRes]) => {
        if (!cancelled) {
          setDetail({
            plan: planRes.plan,
            shipments: shipmentsRes.data.shipments
          });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErro(
            error instanceof Error
              ? error.message
              : "Falha ao carregar o detalhe da remessa."
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCarregando(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, planId]);

  return (
    <Modal
      open={planId !== null}
      onClose={onClose}
      title="Detalhe da remessa"
      width={680}
    >
      {carregando ? <p role="status">Carregando detalhe…</p> : null}
      {erro !== null ? (
        <p role="alert" style={{ color: "var(--orange)" }}>
          {erro}
        </p>
      ) : null}
      {detail !== null ? (
        <PlanDetailContent detail={detail} />
      ) : null}
    </Modal>
  );
}

function PlanDetailContent({ detail }: { detail: DetailState }): ReactNode {
  const { plan, shipments } = detail;
  const badge = planBadge(plan.status);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <strong style={{ fontSize: 15 }}>{plan.name ?? "(sem nome)"}</strong>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
          {plan.id}
          {referenceSuffix(plan.spApiInboundPlanId, plan.id)}
        </div>
      </div>

      <dl style={detailGridStyle}>
        <Field label="Enviar para" value={deriveDestinationLabel(plan.destinationMarketplaces)} />
        <Field label="Workflow" value={plan.workflowStatus} />
        <Field label="Criado em" value={formatDateTime(plan.createdAt)} />
        <Field label="Última atualização" value={formatDateTime(plan.updatedAt)} />
      </dl>

      <div>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: "0.5rem" }}>
          Rastreamento por caixa
        </div>
        {shipments.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Nenhum shipment registrado para este plano ainda.
          </p>
        ) : (
          shipments.map((shipment) => (
            <ShipmentTracking key={shipment.shipmentId} shipment={shipment} />
          ))
        )}
      </div>
    </div>
  );
}

function ShipmentTracking({ shipment }: { shipment: Shipment }): ReactNode {
  const columns: Column<BoxTracking>[] = [
    {
      key: "boxId",
      header: "Caixa",
      render: (box) => <span className="mono">{box.boxId}</span>
    },
    {
      key: "trackingId",
      header: "Nº de rastreamento",
      render: (box) =>
        box.trackingId !== null ? (
          <span className="mono">{box.trackingId}</span>
        ) : (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        )
    },
    {
      key: "status",
      header: "Status",
      render: (box) =>
        box.status !== null ? (
          <Badge tone="green">{box.status}</Badge>
        ) : (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        )
    }
  ];

  return (
    <div style={{ marginBottom: "0.75rem" }}>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: "0.3rem" }}>
        <span className="mono">{shipment.shipmentId}</span>
        {shipment.destinationFcCode !== null
          ? ` · ${shipment.destinationFcCode}`
          : ""}
      </div>
      <DataTable
        columns={columns}
        rows={shipment.boxTracking}
        getRowKey={(box) => box.boxId}
        empty="Sem caixas rastreadas."
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <dt style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </dt>
      <dd style={{ margin: 0, fontSize: 13 }}>{value}</dd>
    </div>
  );
}

const detailGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "0.75rem 1.25rem",
  margin: 0
} as const;
